import classNames from 'classnames';
import { connect } from 'formik';
import { Component, PropsWithChildren, createRef } from 'react';
import React, { ChangeEvent } from 'react';
import { RegisterElement } from '..';
import { FieldHelpersWrapper, GlyphiconWrapper, wrapperClasses } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { allowedSizes } from '../utils/sizes';
import './Input.css';
import { getDescribedBy } from './inputUtils';
import { IInputState, InnerTextInputProps, ITextInputProps } from './types';

/**
 * Controlled Input
 */
export class Input extends Component<PropsWithChildren<InnerTextInputProps>, IInputState> {
  public static defaultProps = {
    autoExpand: false,
    autoSize: false,
    placeholder: '',
    sizePreset: undefined,
  };

  private Tag: any;
  private id = this.props.id || createId(this.props.type || 'input');

  private inputTag = createRef<HTMLInputElement>();
  private inputTagPlaceholderSizer = createRef<HTMLSpanElement>();
  private inputTagSizer = createRef<HTMLSpanElement>();

  private initialized: boolean = false;

  public constructor(props: InnerTextInputProps) {
    super(props);

    this.Tag = props.type === 'textarea' ? 'textarea' : 'input';

    this.state = {
      contentWidth: undefined,
      initialized: false,
      inputStyles: {},
      inputWidth: 0,
      maximumWidth: undefined,
      minimumWidth: undefined,
    };
  }

  public componentDidUpdate(prevProps: InnerTextInputProps) {
    const { autoSize } = prevProps;

    if (!autoSize && this.props.autoSize) {
      this.copyInputStyles();
      this.autoSize();
    }
  }

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);

    if (this.props.autoSize) {
      this.copyInputStyles();
    }

    this.initialized = true;
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
  }

  public render() {
    const { id } = this;
    const type = this.props.type || 'text';
    const {
      autoExpand,
      autoSize,
      sizePreset,
      transition,
      name = '',
      helpText,
      required = false,
      disabled = false,
      displaySuccess = false,
      maxLength,
      placeholder,
      formik,
      validate,
      valid: inputValid,
      touched: inputTouched,
      errorMessage: inputErrorMessage,
      ...inputAttrsProps
    } = this.props;

    const { valid = true, touched = false, value = '', onBlur } = resolveProps(this.props, this.props.formik);

    const showError = touched && !valid;

    const showSuccess = displaySuccess && value !== '' && touched && valid;

    const inputClasses = classNames({
      'form-control': type !== 'file',
    });

    const describedBy = getDescribedBy({
      hasErrors: showError,
      hasHelpText: !!helpText,
      id,
    });

    const Tag = this.Tag;

    const conditionalTagProps: {
      onKeyDown?: () => {};
    } = {};

    if (autoSize && autoExpand) {
      conditionalTagProps.onKeyDown = () => setTimeout(this.autoSize, 0);
    }

    const tagStyles: {
      maxWidth?: number | string;
      opacity?: number;
      transitionDuration?: string;
      transitionProperty?: string;
      width?: number | string;
    } = {};

    if (sizePreset) {
      const sizeWidth = allowedSizes[sizePreset];

      if (sizeWidth) {
        tagStyles.maxWidth = sizeWidth;
      }
    }

    if (autoSize) {
      tagStyles.opacity = this.initialized ? 1 : 0;
      // Hard-code some extra space to the right for validation status
      tagStyles.width = this.state.inputWidth + 34;
    }

    if (transition) {
      tagStyles.transitionDuration = '100';
      tagStyles.transitionProperty = 'width';
    }

    const containerClasses = wrapperClasses({
      className: 'hlc-input-wrapper',
      showError,
      showSuccess,
    });

    return (
      <RegisterElement name={`register-${id}`}>
        {(ref: any) => {
          const myRef = showError ? ref : undefined;
          return (
            <div ref={myRef} id={`${id}-wrapper`} className={containerClasses} qa-id={this.props['qa-id']}>
              <FieldHelpersWrapper id={id} props={this.props}>
                {/* Placeholder Sizer */}
                {autoSize && (
                  <span
                    className="placeholder-sizer"
                    aria-hidden={true}
                    ref={this.inputTagPlaceholderSizer}
                    style={{
                      ...this.state.inputStyles,
                      opacity: 0,
                      pointerEvents: 'none',
                      position: 'absolute',
                    }}
                  >
                    {placeholder}
                  </span>
                )}
                {/* Input Sizer */}
                {autoSize && (
                  <span
                    className="input-sizer"
                    aria-hidden={true}
                    ref={this.inputTagSizer}
                    style={{
                      ...this.state.inputStyles,
                      opacity: 0,
                      pointerEvents: 'none',
                      position: 'absolute',
                    }}
                  >
                    {/* Track the input's value, but replace spaces with
                non-breaking spaces so they aren't collapsed, and add some
                padding to the end */}
                    {String(value).replace(/ /g, String.fromCharCode(160))}
                  </span>
                )}
                <GlyphiconWrapper {...{ showError, showSuccess, valid }} style={tagStyles}>
                  <Tag
                    {...conditionalTagProps}
                    {...inputAttrsProps}
                    ref={this.inputTag}
                    id={id}
                    name={name}
                    type={type}
                    onChange={this.handleChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    required={!!required}
                    value={value}
                    className={inputClasses}
                    aria-invalid={!valid}
                    aria-describedby={describedBy}
                    disabled={disabled}
                    maxLength={maxLength}
                  />
                </GlyphiconWrapper>{' '}
              </FieldHelpersWrapper>
            </div>
          );
        }}
      </RegisterElement>
    );
  }

  /**
   * Copy relevant styles from the input tag to use for our invisible sizers
   */
  private copyInputStyles(): void {
    const { current: inputTag } = this.inputTag;

    if (!inputTag) {
      return;
    }

    const styles = getComputedStyle(inputTag);
    const relevantStyles = ['border', 'color', 'font', 'padding'];

    const filteredStyles = Object.keys(styles)
      // Grab only styles in the relevantStyles list
      .filter((key) => relevantStyles.includes(key))
      // Turn that list into a new styles object
      .reduce((obj, key) => {
        obj[key] = styles[key];
        return obj;
      }, {});

    this.setState(
      {
        inputStyles: filteredStyles,
      },
      () => {
        // Let browser rendering processes catch up
        // @see https://stackoverflow.com/a/779785/399077
        setTimeout(this.autoSize, 0);
      },
    );
  }

  private autoSize = (): void => {
    const { type } = this.props;
    const { current: inputTagSizer } = this.inputTagSizer;
    const { current: inputTagPlaceholderSizer } = this.inputTagPlaceholderSizer;

    if (!inputTagSizer || !inputTagPlaceholderSizer) {
      return;
    }

    const feedback = inputTagSizer.querySelector('.form-control-feedback');

    // Make sure width is at least as big as the placeholder
    let inputWidth = Math.max(inputTagSizer.offsetWidth, inputTagPlaceholderSizer.offsetWidth);

    const wrapper = inputTagSizer.closest('[class$="-wrapper"]') as HTMLElement;
    const maximumWidth = wrapper ? wrapper.offsetWidth : inputWidth;

    // Make sure width is no larger than the maximum
    inputWidth = Math.min(inputWidth, maximumWidth || inputWidth);

    if (type === 'number') {
      // Add extra space for controls
      inputWidth += 20;
    }

    this.setState({ inputWidth });
  };

  private handleChange = (e: ChangeEvent<any>) => {
    const { formik, onChange, name } = this.props;
    const { value } = e.target;

    if (onChange) {
      onChange(name, value);
    } else if (formik && formik.handleChange) {
      formik.handleChange(e);
    }
  };
}

export default connect<ITextInputProps>(Input);
